import { AfterViewInit, Component, EventEmitter, Inject, Input, Output, ViewChild } from '@angular/core';

import { Subject, combineLatest } from 'rxjs';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { MessageCollationneDTO } from '../entities/message-collationne-dto.entity';
import { MessageCollationneService } from '../services/message-collationne.service';

import { ParcService } from '../../structure/parc/services/parc.service';
import { ParcTLR } from '../../structure/parc/entities/parc-tlr.entity';

import { JSR310ConversionService } from '../../../shared/jsr310-localdates/services/jsr310-conversion.service';

class Row {
	constructor(
		public message: MessageCollationneDTO,
		public numero: string,
		public parcsLabels: string[],
	) { }
}

@Component({
	selector: 'messages-collationnes-table',
	templateUrl: 'messages-collationnes-table.html',
	styleUrls: ['messages-collationnes-table.css']
})
export class MessagesCollationnesTableComponent implements AfterViewInit {
	// Elements de fonctionnement du tableau
	rows: Row[] = [];
	displayedColumns: string[] = ['parcInfo', 'emetteur', 'dateMessage', 'nomAgent', 'agentNum', 'correspondantEntite', 'correspondantNom', 'correspondantNum'];
	dataSource: MatTableDataSource<Row> = new MatTableDataSource(this.rows);
	
	// Contexte de donnees (utilise dans l'affichage)
	messagesSubject = new Subject<MessageCollationneDTO[]>();
	parcsById!: Map<number, ParcTLR>;

	// Fonctions d'aide
	getParcLabel = (parc: ParcTLR) => parc.code + ' (' + (parc.description ? parc.description : '') + ')';

	constructor( //
		@Inject(JSR310ConversionService) public jsr310Service: JSR310ConversionService,
		@Inject(MessageCollationneService) public messageService: MessageCollationneService,
		@Inject(ParcService) parcService: ParcService,
	) {
		let parcsById$ = parcService.getParcTLRMapById();
		
		// Definir comment on combine les donnees messages avec les donnees generales pour determiner les informations a afficher
		combineLatest([parcsById$, this.messagesSubject]).subscribe(
			data => {
				let parcsById = data[0];
				let messages = data[1];
				
				// Construction des lignes du tableau
				let rows: Row[] = [];
				for (let message of messages) {
					let numero = this.messageService.getNumero(message);
					
					let parcsIds = message.parcsIds;
					let parcs = parcsIds.map((id) => parcsById.get(id));
					let parcsLabels = parcs.map((parc) => parc ? this.getParcLabel(parc) : "<unknown>");
					parcsLabels.sort();
					if (parcsLabels && parcsLabels.length === 0 && message.parcEnConstruction) {
						parcsLabels.push(message.parcEnConstruction);
					}
					let row = new Row(message, numero, parcsLabels);
					rows.push(row);
				}
				
				this.rows = rows;

				// Mise a jour du dataSource
				this.dataSource.data = this.rows;
			}
		)
	}

	// Elements injectes necessaires a la gestion du tableau
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;
	
	@Input() set messages(messages: MessageCollationneDTO[]) {
		this.messagesSubject.next(messages);
	}
	
	@Output() rowClick = new EventEmitter<MessageCollationneDTO>();

	ngAfterViewInit() {
		// Initialisation du dataSource
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;

		// Preparation de l'accesseur particulier pour le tri des proprietes LocalDate
		this.dataSource.sortingDataAccessor = (row, property) => {
			switch (property) {
				case 'parcInfo': {
					const values = row.parcsLabels;
					return values.join(",");
				}
				case 'dateMessage': {
					const value = row.message.dateMessage;
					const valueAsJSDate = this.jsr310Service.fromNullableLocalDateTime(value);
					return valueAsJSDate;
				}
				case 'nomAgent': {
					return [row.message.agentNom, row.message.agentPrenom].join(' ');
				}
				case 'agentNom': {
					return '';
				}
				default: {
					// NB: on doit de-typer row.message sinon on ne peut pas acceder a ses propietes par leur nom en string
					const value = (row.message as { [key: string]: any })[property];
					return value;
				}
			}
		};
	}
	
	onRowClick(message: MessageCollationneDTO) {
		this.rowClick.emit(message);
	}
}